/**
 * Nutrition Calculation Utilities
 * Provides smart defaults for nutritional information based on category and serving size
 */

/**
 * Category-based nutritional defaults (per 100g/100ml)
 */
const NUTRITION_DEFAULTS = {
  rice: { calories: 130, protein: 3, carbs: 28, fat: 0.3, fiber: 0.4 },
  biryani: { calories: 180, protein: 6, carbs: 30, fat: 5, fiber: 1 },
  curry: { calories: 150, protein: 8, carbs: 10, fat: 9, fiber: 2 },
  gravy: { calories: 150, protein: 8, carbs: 10, fat: 9, fiber: 2 },
  bread: { calories: 120, protein: 4, carbs: 22, fat: 2, fiber: 3 },
  roti: { calories: 120, protein: 4, carbs: 22, fat: 2, fiber: 3 },
  naan: { calories: 140, protein: 4, carbs: 24, fat: 3, fiber: 2 },
  paratha: { calories: 160, protein: 4, carbs: 25, fat: 5, fiber: 2 },
  snacks: { calories: 200, protein: 6, carbs: 20, fat: 10, fiber: 2 },
  starters: { calories: 200, protein: 6, carbs: 20, fat: 10, fiber: 2 },
  appetizers: { calories: 200, protein: 6, carbs: 20, fat: 10, fiber: 2 },
  dessert: { calories: 250, protein: 4, carbs: 40, fat: 8, fiber: 1 },
  sweet: { calories: 250, protein: 4, carbs: 40, fat: 8, fiber: 1 },
  beverage: { calories: 50, protein: 0, carbs: 12, fat: 0, fiber: 0 },
  drink: { calories: 50, protein: 0, carbs: 12, fat: 0, fiber: 0 },
  juice: { calories: 45, protein: 0.5, carbs: 11, fat: 0, fiber: 0.5 },
  dal: { calories: 140, protein: 9, carbs: 20, fat: 4, fiber: 8 },
  lentil: { calories: 140, protein: 9, carbs: 20, fat: 4, fiber: 8 },
  soup: { calories: 60, protein: 3, carbs: 8, fat: 2, fiber: 1 },
  salad: { calories: 30, protein: 2, carbs: 5, fat: 0.5, fiber: 2 },
  pasta: { calories: 160, protein: 6, carbs: 30, fat: 1, fiber: 2 },
  noodles: { calories: 140, protein: 5, carbs: 27, fat: 2, fiber: 1 },
  pizza: { calories: 250, protein: 10, carbs: 30, fat: 10, fiber: 2 },
  burger: { calories: 280, protein: 12, carbs: 30, fat: 12, fiber: 2 },
  sandwich: { calories: 220, protein: 10, carbs: 28, fat: 8, fiber: 3 },
  default: { calories: 150, protein: 5, carbs: 20, fat: 5, fiber: 2 }
};

/**
 * Common allergens list
 */
export const ALLERGEN_OPTIONS = [
  { value: 'dairy', label: '🥛 Dairy', emoji: '🥛' },
  { value: 'eggs', label: '🥚 Eggs', emoji: '🥚' },
  { value: 'peanuts', label: '🥜 Peanuts', emoji: '🥜' },
  { value: 'tree_nuts', label: '🌰 Tree Nuts', emoji: '🌰' },
  { value: 'soy', label: 'Soy', emoji: '🫘' },
  { value: 'wheat', label: '🌾 Wheat/Gluten', emoji: '🌾' },
  { value: 'fish', label: '🐟 Fish', emoji: '🐟' },
  { value: 'shellfish', label: '🦐 Shellfish', emoji: '🦐' },
  { value: 'sesame', label: 'Sesame', emoji: '🌱' }
];

/**
 * Serving unit options
 */
export const SERVING_UNIT_OPTIONS = [
  { value: 'g', label: 'Grams (g)' },
  { value: 'ml', label: 'Milliliters (ml)' },
  { value: 'piece', label: 'Piece' },
  { value: 'bowl', label: 'Bowl' },
  { value: 'plate', label: 'Plate' }
];

/**
 * Convert serving size to grams for calculation
 */
function convertToGrams(servingSize, servingUnit) {
  const size = parseFloat(servingSize);
  
  switch (servingUnit) {
    case 'ml':
      return size; // Approximate: 1ml ≈ 1g for most liquids
    case 'piece':
      return 100; // Average piece
    case 'bowl':
      return 250; // Average bowl
    case 'plate':
      return 300; // Average plate
    case 'g':
    default:
      return size;
  }
}

/**
 * Get category key from category name
 */
function getCategoryKey(categoryName) {
  if (!categoryName) return 'default';
  
  const name = categoryName.toLowerCase();
  
  // Check for exact matches first
  if (NUTRITION_DEFAULTS[name]) {
    return name;
  }
  
  // Check for partial matches
  for (const key of Object.keys(NUTRITION_DEFAULTS)) {
    if (name.includes(key) || key.includes(name)) {
      return key;
    }
  }
  
  return 'default';
}

/**
 * Calculate nutritional defaults based on category and serving size
 * @param {string} categoryName - Name of the category
 * @param {number} servingSize - Numeric serving size
 * @param {string} servingUnit - Unit of serving (g, ml, piece, bowl, plate)
 * @returns {object} Nutritional information
 */
export function calculateNutritionDefaults(categoryName, servingSize, servingUnit = 'g') {
  if (!servingSize || servingSize <= 0) {
    return null;
  }
  
  const servingGrams = convertToGrams(servingSize, servingUnit);
  const baseMultiplier = servingGrams / 100;
  
  const categoryKey = getCategoryKey(categoryName);
  const baseValues = NUTRITION_DEFAULTS[categoryKey];
  
  return {
    calories: Math.round(baseValues.calories * baseMultiplier),
    protein: Math.round(baseValues.protein * baseMultiplier * 10) / 10,
    fat: Math.round(baseValues.fat * baseMultiplier * 10) / 10,
    carbs: Math.round(baseValues.carbs * baseMultiplier * 10) / 10,
    fiber: Math.round(baseValues.fiber * baseMultiplier * 10) / 10,
    per_serving: true,
    auto_calculated: true
  };
}

/**
 * Check if nutrition values are provided by user
 */
export function hasCustomNutrition(nutrition) {
  if (!nutrition) return false;
  
  return !!(
    nutrition.calories ||
    nutrition.protein ||
    nutrition.fat ||
    nutrition.carbs ||
    nutrition.fiber
  );
}

/**
 * Calculate platform price from actual price
 * Always rounds UP to the next whole number (ceiling)
 * Examples: 186.16 → 187, 186.1 → 187, 190.6 → 191
 * @param {number} actualPrice - Restaurant's base price
 * @param {number} gatewayFeePercentage - Payment gateway fee (default 2%)
 * @returns {number} Platform price (rounded up to whole number)
 */
export function calculatePlatformPrice(actualPrice, gatewayFeePercentage = 2) {
  if (!actualPrice || actualPrice <= 0) return 0;
  
  const price = parseFloat(actualPrice);
  const fee = gatewayFeePercentage / 100;
  const platformPrice = price * (1 + fee);
  
  // Always round UP to next whole number
  return Math.ceil(platformPrice);
}

/**
 * Calculate default display price with smart psychological pricing
 * Creates a natural-looking discount (5-20%) with prices ending in 9
 * @param {number} platformPrice - Platform price
 * @returns {number} Suggested display price ending in 9
 */
export function calculateDefaultDisplayPrice(platformPrice) {
  if (!platformPrice || platformPrice <= 0) return 0;
  
  const price = parseFloat(platformPrice);
  
  // Target discount range: 5-20%
  // We'll aim for 10-15% as the sweet spot
  const targetDiscountPercent = 12; // 12% discount looks natural
  
  // Calculate the display price that would give us the target discount
  // Formula: displayPrice = platformPrice / (1 - discountPercent/100)
  const calculatedDisplay = price / (1 - targetDiscountPercent / 100);
  
  // Now round to the nearest number ending in 9
  const roundedDisplay = roundToNine(calculatedDisplay);
  
  // Verify the discount is within 5-20% range
  const actualDiscount = ((roundedDisplay - price) / roundedDisplay) * 100;
  
  // If discount is too low (< 5%), bump up to next "9" number
  if (actualDiscount < 5) {
    return roundToNine(calculatedDisplay + 10);
  }
  
  // If discount is too high (> 20%), reduce to previous "9" number
  if (actualDiscount > 20) {
    return roundToNine(calculatedDisplay - 10);
  }
  
  return roundedDisplay;
}

/**
 * Round a number to end in 9 (psychological pricing)
 * Examples: 104 → 109, 115 → 119, 195 → 199
 * @param {number} value - Value to round
 * @returns {number} Rounded value ending in 9
 */
function roundToNine(value) {
  if (value <= 0) return 9;
  
  // For values under 10, just return 9
  if (value < 10) return 9;
  
  // Get the magnitude (10, 100, 1000, etc.)
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  
  // Round to nearest 10
  const roundedToTen = Math.ceil(value / 10) * 10;
  
  // Subtract 1 to get the "9" ending
  const endingInNine = roundedToTen - 1;
  
  // Ensure it's greater than the original value
  if (endingInNine < value) {
    return roundedToTen + 9; // Go to next "9" number
  }
  
  return parseFloat(endingInNine.toFixed(2));
}

/**
 * Calculate discount percentage
 * @param {number} displayPrice - Menu display price
 * @param {number} platformPrice - Platform price
 * @returns {number} Discount percentage
 */
export function calculateDiscountPercentage(displayPrice, platformPrice) {
  if (!displayPrice || !platformPrice || displayPrice <= platformPrice) {
    return 0;
  }
  
  return Math.round(((displayPrice - platformPrice) / displayPrice) * 100);
}

/**
 * Get pricing breakdown for display
 * @param {number} actualPrice - Restaurant's base price
 * @param {number} platformPrice - Platform price (auto-calculated)
 * @param {number} displayPrice - Menu display price (optional)
 * @param {number} gatewayFeePercentage - Payment gateway fee (default 2%)
 * @returns {object} Pricing breakdown
 */
export function getPricingBreakdown(actualPrice, platformPrice, displayPrice, gatewayFeePercentage = 2) {
  const actual = parseFloat(actualPrice) || 0;
  const platform = parseFloat(platformPrice) || 0;
  const display = parseFloat(displayPrice) || 0;
  
  const gatewayFee = platform - actual;
  const finalDisplay = display || calculateDefaultDisplayPrice(platform);
  const discountPercent = calculateDiscountPercentage(finalDisplay, platform);
  
  return {
    actualPrice: actual.toFixed(2),
    paymentGatewayFee: gatewayFee.toFixed(2),
    platformCommission: gatewayFee.toFixed(2), // kept for backwards compatibility
    platformPrice: platform.toFixed(2),
    menuDisplayPrice: finalDisplay.toFixed(2),
    customerDiscountPercent: discountPercent,
    restaurantReceives: actual.toFixed(2),
    showDiscount: finalDisplay > platform
  };
}

/**
 * Validate menu item pricing
 * @param {number} actualPrice - Restaurant's base price
 * @param {number} platformPrice - Platform price
 * @param {number} displayPrice - Menu display price (optional)
 * @returns {object} Validation result
 */
export function validatePricing(actualPrice, platformPrice, displayPrice) {
  const errors = [];
  
  if (!actualPrice || actualPrice < 1) {
    errors.push('Actual price must be at least ₹1');
  }
  
  if (platformPrice && platformPrice < actualPrice) {
    errors.push('Platform price must be greater than or equal to actual price');
  }
  
  if (displayPrice && platformPrice && displayPrice < platformPrice) {
    errors.push(`Menu display price must be at least ₹${platformPrice.toFixed(2)} (platform price)`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate serving information
 * @param {number} servingSize - Serving size
 * @param {string} servingUnit - Serving unit
 * @returns {object} Validation result
 */
export function validateServing(servingSize, servingUnit) {
  const errors = [];
  
  if (!servingSize || servingSize <= 0) {
    errors.push('Serving size is mandatory and must be greater than 0');
  }
  
  if (!servingUnit) {
    errors.push('Serving unit is required');
  }
  
  const validUnits = ['g', 'ml', 'piece', 'bowl', 'plate'];
  if (servingUnit && !validUnits.includes(servingUnit)) {
    errors.push('Invalid serving unit');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Format allergen display name
 */
export function formatAllergenName(allergen) {
  const allergenMap = {
    dairy: '🥛 Dairy',
    eggs: '🥚 Eggs',
    peanuts: '🥜 Peanuts',
    tree_nuts: '🌰 Tree Nuts',
    soy: '🫘 Soy',
    wheat: '🌾 Wheat/Gluten',
    fish: '🐟 Fish',
    shellfish: '🦐 Shellfish',
    sesame: '🌱 Sesame'
  };
  
  return allergenMap[allergen] || allergen;
}

/**
 * Get default serving size based on category
 */
export function getDefaultServingSize(categoryName) {
  if (!categoryName) return { size: 200, unit: 'g' };
  
  const name = categoryName.toLowerCase();
  
  if (name.includes('rice') || name.includes('biryani')) {
    return { size: 300, unit: 'g' };
  }
  if (name.includes('curry') || name.includes('gravy')) {
    return { size: 250, unit: 'g' };
  }
  if (name.includes('bread') || name.includes('roti') || name.includes('naan')) {
    return { size: 100, unit: 'g' };
  }
  if (name.includes('snack') || name.includes('starter')) {
    return { size: 150, unit: 'g' };
  }
  if (name.includes('dessert') || name.includes('sweet')) {
    return { size: 120, unit: 'g' };
  }
  if (name.includes('beverage') || name.includes('drink') || name.includes('juice')) {
    return { size: 250, unit: 'ml' };
  }
  
  return { size: 200, unit: 'g' };
}

export default {
  calculateNutritionDefaults,
  hasCustomNutrition,
  calculatePlatformPrice,
  calculateDefaultDisplayPrice,
  calculateDiscountPercentage,
  getPricingBreakdown,
  validatePricing,
  validateServing,
  formatAllergenName,
  getDefaultServingSize,
  ALLERGEN_OPTIONS,
  SERVING_UNIT_OPTIONS
};
